import React, { useState, useEffect } from 'react';
import { Box, Paper, TextField, Button, Typography, Container, InputAdornment } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks/reduxHooks';
import { login } from '../store/slices/authSlice';
import { showSuccess, showError } from '../store/slices/toastSlice';
import { authApi } from '../services/authApi';
import { config } from '../config';

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<{ username?: string; password?: string }>({});

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isLoading, error, isAuthenticated } = useAppSelector((state) => state.auth);

  useEffect(() => {
    // Redirect if already authenticated
    if (isAuthenticated) {
      navigate('/admin/dashboard');
    }
  }, [isAuthenticated, navigate]);


  const validateForm = () => {
    const newErrors: { username?: string; password?: string } = {};

    // Username validation
    if (!username.trim()) {
      newErrors.username = 'Username is required';
    } else if (username.length < config.validation.username.minLength) {
      newErrors.username = `Username must be at least ${config.validation.username.minLength} characters`;
    } else if (username.length > config.validation.username.maxLength) {
      newErrors.username = `Username must be less than ${config.validation.username.maxLength} characters`;
    }

    // Password validation
    if (!password.trim()) {
      newErrors.password = 'Password is required';
    } else if (password.length < config.validation.password.minLength) {
      newErrors.password = `Password must be at least ${config.validation.password.minLength} characters`;
    }

    setErrors(newErrors);
    
    // Show validation toast if there are errors
    if (Object.keys(newErrors).length > 0) {
      dispatch(showError({
        title: 'Validation Error',
        message: 'Please fix the form errors before submitting.',
        duration: 4000,
      }));
    }
    
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await dispatch(login({ username, password })).unwrap();
      dispatch(showSuccess({
        title: 'Login Successful',
        message: `Welcome back, ${username}!`,
        duration: 3000,
      }));
      // Redirect to dashboard after successful login
      navigate('/admin/dashboard');
    } catch (error: any) {
      dispatch(showError({
        title: 'Login Failed',
        message: error.message || 'Invalid username or password. Please try again.',
        duration: 5000,
      }));
    }
  };


  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      px: 2,
      background: (t) => t.palette.mode === 'dark'
        ? 'radial-gradient(1200px 400px at -10% -10%, rgba(28,233,224,0.08), transparent), radial-gradient(1000px 300px at 110% 110%, rgba(255,115,22,0.08), transparent)'
        : 'radial-gradient(1200px 400px at -10% -10%, rgba(14,25,24,0.06), transparent)'
    }}>
      <div className="floating-shape shape-1"></div>
      <Paper sx={{
        width: '100%',
        maxWidth: 420,
        p: { xs: 3, sm: 4 },
        border: (t) => `1px solid ${t.palette.divider}`,
        borderBottom: (t) => `2px solid ${t.palette.secondary.main}40`,
        boxShadow: (t) => `0 14px 40px ${t.palette.mode === 'dark' ? 'rgba(28,233,224,0.15)' : 'rgba(2,6,23,0.06)'}`,
      }}>
        <Box textAlign="center" mb={2}>
          <Typography variant="h4" sx={{ fontFamily: 'Aldrich, sans-serif', fontWeight: 700 }}>
            Asknow
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Sign in to continue
          </Typography>
        </Box>

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="username"
            label="Username"
            name="username"
            autoComplete="username"
            autoFocus
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            error={!!errors.username}
            helperText={errors.username}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ mr: 1.25, color: 'text.disabled' }}>
                  <PersonIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            type="password"
            id="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={!!errors.password}
            helperText={errors.password}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ mr: 1.25, color: 'text.disabled' }}>
                  <LockIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3 }}
            disabled={isLoading}
          >
            {isLoading ? 'Signing In…' : 'Sign In'}
          </Button>
        </Box>
      </Paper>
      <div className="floating-shape shape-2"></div>
    </Box>
  );
};
export default Login;
